"use client";

import { motion } from "motion/react";
import React from "react";
import BannerTitles from "./BannerTitles";

type MegaCard = {
  title: string;
  description: string | string[];
};

type CaseStudyEngageMentModelProps = {
  leftImage?: string;
  imageAlt?: string;
  cards?: MegaCard[];
  containerClassName?: string;
  // ✅ Make subtitle & description reusable
  subtitle?: string;
  description?: string;
};

// ✅ Helper to render description grid
const DescriptionGrid: React.FC<{ description: string | string[] }> = ({
  description,
}) => {
  if (!Array.isArray(description)) return <>{description}</>;

  const len = description.length;

  return (
    <div
      className={`grid gap-4 ${len === 1
        ? "grid-cols-1"
        : len === 2
          ? "grid-cols-1 md:grid-cols-2"
          : len === 3
            ? "grid-cols-2 grid-rows-2"
            : "grid-cols-2"
        }`}
    >
      {description.map((para, i) => (
        <p
          key={i}
          className={`w-fit border px-3 py-1 rounded-lg bg-[#ccc]/12 border-[#ccc]/48 
         ${len === 3 && i === 2 ? "col-span-2" : ""}`}
        >
          {para.charAt(0).toUpperCase() + para.slice(1)}
        </p>

      ))}
    </div>
  );
};

const CaseStudyEngageMentModel: React.FC<CaseStudyEngageMentModelProps> = ({
  cards = [],
  containerClassName,
  subtitle = "Engagement Model",
  description = "Functions & Team involved", // ✅ default
}) => {
  const maxCards = 8;
  const limitedCards = cards.slice(0, maxCards);

  const buildRows = (cards: MegaCard[], perRow: number) => {
    const result: MegaCard[][] = [];
    for (let i = 0; i < cards.length; i += perRow) {
      result.push(cards.slice(i, i + perRow));
    }
    return result;
  };

  return (
    <section className={`w-full ${containerClassName || ""}`}>
      <div className="w-full section-background-filler p-4 sm:p-8 xl:p-0">
        <div className="flex flex-col xl:flex-row items-stretch">
          {/* Left panel */}
          <div className="w-full xl:w-[25%] mb-6 xl:mb-0">
            <div className="mx-auto px-4 xl:ml-32 flex flex-col justify-center items-start h-full">
              <BannerTitles
                title={subtitle}
                description={description} // ✅ from prop
                align="left"
              />
            </div>
          </div>

          {/* Right Content */}
          <div className="w-full xl:w-[75%] flex relative">
            {/* Diagonal background overlay (xl only) */}
            <div
              className="hidden xl:block absolute top-0 left-0 w-full h-full bg-white/84 backdrop-blur-sm"
              style={{
                clipPath: "polygon(20% 0%, 100% 0%, 100% 100%, 10% 100%)",
                zIndex: 0,
              }}
            />

            {/* Cards container */}
            <div className="w-full relative z-10">

              <div className="hidden xl:block absolute -bottom-14   left-4">
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="280"
                  height="384"
                  viewBox="0 0 280 384"
                  fill="none"
                >
                  <g id="QuoteFrame">
                    <g id="Group 1321314691">
                      <g id="Rectangle 34624418" filter="url(#filter0_d_487_350)">
                        <path
                          d="M129.083 127.5H143.999L84.9609 328H70.041L129.083 127.5Z"
                          fill="#D53333"
                        />
                      </g>
                      <g id="Group 1321314692">
                        <g id="Rectangle 34624418_2" filter="url(#filter1_d_487_350)">
                          <path
                            d="M107.388 127.5H122.304L63.2656 328H48.3457L107.388 127.5Z"
                            fill="#D53333"
                          />
                        </g>
                      </g>
                    </g>



                  </g>


                </motion.svg>
              </div>
              {/* sm → 1 column stacked */}
              <div className="flex flex-col gap-6 sm:hidden">
                {limitedCards.map((card, i) => (
                  <div
                    key={i}
                    className="w-full p-6 lg:p-8 border border-[#ccc]/48 rounded-lg bg-white"
                  >
                    <h3 className="text-[#272d57] text-lg md:text-xl font-semibold">
                      {card.title}
                    </h3>
                    <div className="mt-2 text-[#1b1b1b] text-sm md:text-base leading-relaxed">
                      <DescriptionGrid description={card.description} />
                    </div>
                  </div>
                ))}
              </div>

              {/* sm → lg → 2-column odd/even */}
              <div className="hidden sm:grid lg:hidden grid-cols-2 gap-6">
                {buildRows(limitedCards, 2).map((rowCards) =>
                  rowCards.map((card, i) => (
                    <div
                      key={i}
                      className={`p-6 lg:p-8 border border-[#ccc]/48 rounded-lg bg-white ${rowCards.length === 1 ? "col-span-2" : "col-span-1"
                        }`}
                    >
                      <h3 className="text-[#272d57] text-lg md:text-xl font-semibold">
                        {card.title}
                      </h3>
                      <div className="mt-2 text-[#1b1b1b] text-sm md:text-base leading-relaxed">
                        <DescriptionGrid description={card.description} />
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* lg → xl → 3-column odd/even */}
              <div className="hidden lg:grid xl:hidden grid-cols-3 gap-6">
                {buildRows(limitedCards, 3).map((rowCards) =>
                  rowCards.map((card, i) => (
                    <div
                      key={i}
                      className={`p-6 lg:p-8 border border-[#ccc]/48 rounded-lg bg-white ${rowCards.length < 3 ? "col-span-3" : "col-span-1"
                        }`}
                    >
                      <h3 className="text-[#272d57] text-lg md:text-xl font-semibold">
                        {card.title}
                      </h3>
                      <div className="mt-2 text-[#1b1b1b] text-sm md:text-base leading-relaxed">
                        <DescriptionGrid description={card.description} />
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* xl+ → custom 5-column layout */}
              <div className="hidden xl:grid grid-cols-5 auto-rows-auto bg-transparent">
                {buildRows(limitedCards, 2).map((rowCards, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    {rowCards.length === 2 && <div className="xl:col-span-1" />}
                    {rowCards[0] && (
                      <div
                        className={`${rowCards.length === 1
                          ? "col-span-4 col-start-2"
                          : "col-span-2"
                          } p-6 lg:p-8 border border-[#ccc]/48 border-l-0 border-r-0 border-b-0`}
                      >
                        <h3 className="text-[#272d57] text-lg md:text-xl font-semibold">
                          {rowCards[0].title}
                        </h3>
                        <div className="mt-2 text-[#1b1b1b] text-sm md:text-base leading-relaxed">
                          <DescriptionGrid description={rowCards[0].description} />
                        </div>
                      </div>
                    )}
                    {rowCards[1] && (
                      <div className="col-span-2 p-6 lg:p-8 border border-[#ccc]/48 border-r-0 border-b-0">
                        <h3 className="text-[#272d57] text-lg md:text-xl font-semibold">
                          {rowCards[1].title}
                        </h3>
                        <div className="mt-2 text-[#1b1b1b] text-sm md:text-base leading-relaxed">
                          <DescriptionGrid description={rowCards[1].description} />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudyEngageMentModel;
